import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import { UserMenu } from '../../components/UserMenu';
import { ModelSignupForm } from './ModelSignupForm';
import { ModelSignupFormState, initialModelFormState } from './modelSignup.types';
import { Wordmark } from '../../components/Wordmark';

export function ModelSignupPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [formState, setFormState] = useState<ModelSignupFormState>(initialModelFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdate = (updates: Partial<ModelSignupFormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const token = await getToken();

      // Create FormData for multipart upload (photos)
      const formData = new FormData();
      formData.append('displayName', formState.name);
      formData.append('username', formState.username);
      formData.append('bio', formState.bio);
      formData.append('instagram', formState.instagram);
      formData.append('website', formState.website);
      formData.append('country', formState.country);
      formData.append('isModel', 'true'); // Mark as model profile
      formData.append('modelingStyles', JSON.stringify(formState.modelingStyles));
      formData.append('languages', JSON.stringify(formState.languages));
      formData.append('defaultShowIdentity', String(formState.defaultShowIdentity));
      formData.append('cameraEquipment', formState.cameraEquipment);

      // Append resized photos
      formState.photos.forEach((photo, index) => {
        const blob = photo.resizedBlob || photo.file;
        formData.append('photos', blob, `photo-${index}.jpg`);
      });

      formState.spacePhotos.forEach((photo, index) => {
        const blob = photo.resizedBlob || photo.file;
        formData.append('spacePhotos', blob, `space-photo-${index}.jpg`);
      });

      const response = await fetch('/api/profiles', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create profile');
      }

      const data = await response.json();

      // Navigate to the application submitted page
      navigate('/models/apply/submitted');
    } catch (error) {
      console.error('Error creating profile:', error);
      alert(error instanceof Error ? error.message : 'Failed to create profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper relative z-10">
      {/* Header */}
      <header className="border-b border-line chrome-gradient">
        <div className="page-container h-14 flex items-center gap-5">
          <Wordmark />
          <div className="w-px h-5 bg-line" />
          <span className="text-sm text-text-dim">Model registration</span>
          <div className="flex-1" />
          <UserMenu />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        <ModelSignupForm
          formState={formState}
          onUpdate={handleUpdate}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/')}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
}
